import React, { FC, useMemo } from "react";
import classNames from "classnames";
import Paper from "@material-ui/core/Paper";
import theme from "styles/theme";
import { ISelectionState } from "helpers/selection-state";
import Slide from "@material-ui/core/Slide";
import { Theme, makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/CloseRounded";
import { format } from "lib/globalize-util";
import { FormatMessage } from "react-globalize";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { isMobile, isPhablet } from "constants/media";
import PartialIcon from "@material-ui/icons/IndeterminateCheckBox";

interface IProps {
    selection: ISelectionState;
    viewport: string;
    classes?: {
        root?: string;
        close?: string;
        iconClose?: string;
    };
    dataTest?: string;
    onClose(): void;
    onSelect?(): void;
}

const ActionsToolbar: FC<IProps> = ({ selection, viewport, onSelect, onClose, classes, dataTest, children }) => {
    const inner = useStyles({ all: selection.allSelected && !selection.isFinite, select: !!onSelect });
    const elements = useMemo(() => ({ strong: <strong className={inner.strong} /> }), []); // eslint-disable-line react-hooks/exhaustive-deps
    return (
        <Slide direction="down" in={!!selection.size} mountOnEnter={true} unmountOnExit={true}>
            <Paper elevation={3} className={classNames(inner.root, classes?.root)} data-test={dataTest}>
                <Grid className={inner.grid} container={true} direction="row" alignItems="center" wrap="nowrap">
                    <Grid item={true}>
                        <IconButton
                            className={classes?.close}
                            aria-label="close"
                            onClick={onClose}
                            data-test={dataTest ? `${dataTest}-close` : undefined}
                        >
                            <CloseIcon className={classNames(inner.iconClose, classes?.iconClose)} />
                        </IconButton>
                    </Grid>
                    <Grid item={true} container={true} xs={true} justify="space-around">
                        {!!onSelect && (
                            <div className={inner.selectContainer}>
                                <FormControlLabel
                                    checked={selection.allSelected}
                                    onChange={selection.allSelected ? onClose : onSelect}
                                    classes={{ root: inner.select, label: inner.selectLabel }}
                                    label={
                                        isMobile(viewport) ? (
                                            format("Select all")
                                        ) : (
                                            <FormatMessage path="SELECT_ALL_THAT_MEETS_CRITERIA" elements={elements} />
                                        )
                                    }
                                    control={
                                        <Checkbox
                                            className={inner.checkbox}
                                            checkedIcon={
                                                !selection.isFinite && !selection.isEmptyList() ? (
                                                    <PartialIcon />
                                                ) : undefined
                                            }
                                        />
                                    }
                                />
                            </div>
                        )}
                        <div className={inner.countContainer}>
                            {!onSelect || !isMobile(viewport) ? (
                                <FormatMessage
                                    className={inner.count}
                                    path="[strong]|count|[strong] Selected"
                                    variables={{
                                        count:
                                            selection.isFinite || !selection.allSelected
                                                ? selection.size
                                                : format("All"),
                                    }}
                                    elements={elements}
                                />
                            ) : (
                                <span className={inner.badge}>
                                    {selection.isFinite || !selection.allSelected ? selection.size : format("All")}
                                </span>
                            )}
                        </div>
                    </Grid>
                    {!isPhablet(viewport) && (
                        <Grid item={true} className={inner.actionLabel}>
                            {format("Actions")}:
                        </Grid>
                    )}
                    <Grid item={true}>{children}</Grid>
                </Grid>
            </Paper>
        </Slide>
    );
};

const useStyles = makeStyles<Theme, { all: boolean; select: boolean }>({
    root: {
        zIndex: 1050,
        position: "fixed",
        top: 0,
        left: "0px",
        right: "0px",
        height: theme.appBar.height,
        backgroundColor: theme.palette.lightestGrey,
        borderRadius: 0,
        padding: "5px 10px",
    },
    grid: {
        height: "100%",
    },
    iconClose: {
        width: 24,
        height: 24,
        color: theme.palette.darkerGrey,
    },
    selectContainer: {
        display: "flex",
        flexGrow: 3,
        justifyContent: "space-around",
    },
    select: {
        margin: 0,
        maxWidth: 275,
    },
    selectLabel: {
        fontSize: 14,
        paddingLeft: 10,
        color: "#676767",
        fontFamily: "inherit",
        letterSpacing: "inherit",
    },
    countContainer: ({ all, select }) => ({
        display: "flex",
        opacity: all ? 0 : 1,
        flexGrow: 1,
        transition: "all .2s ease-in-out",
        alignItems: "center",
        justifyContent: select ? "flex-start" : "space-around",
    }),
    count: {
        fontSize: 20,
        color: "#676767",
        flexShrink: 0,
    },
    badge: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: 12,
        minWidth: 24,
        height: 24,
        color: "white",
        backgroundColor: theme.palette.accent1Color,
        borderRadius: "50%",
        padding: 5,
    },
    actionLabel: {
        color: "#676767",
        fontSize: 16,
        marginRight: 10,
    },
    strong: {
        fontWeight: "bold",
    },
    checkbox: {
        backgroundColor: "transparent !important", // force disable ripple effect
        color: theme.palette.grey,
        "& .MuiSvgIcon-root": {
            fontSize: 24,
        },
        "&.Mui-checked": {
            color: theme.palette.primary1Color,
        },
    },
});

export default ActionsToolbar;
